import {
  BOT_SEQUENCE_DEFAULT_BODY_LOCALE,
  BOT_SEQUENCE_STEP_LIMITS,
  BotSequenceConfigStep,
  BotSequenceConfigStepDelivery,
  BotSequenceContentTemplate,
  BotSequenceDeliveryChannel,
  BotSequenceType,
  Channel,
  defaultCopyFor
} from "@swivl-mono/types";
import { Switch, TextArea } from "@swivl-mono/ui";

import { bodiesAreDefault, defaultDelivery, templateGuidance, voicemailAfterTemplate } from "../ladderDraft";
import { LocaleBodiesEditor } from "./LocaleBodiesEditor";
import { RevertToDefault } from "./RevertToDefault";
import { TemplateSelect, confirmTemplateSwitch } from "./TemplateSelect";

interface DeliveryEditorProps {
  channel: BotSequenceDeliveryChannel;
  delivery: BotSequenceConfigStepDelivery | undefined;
  /** For the code-baked defaults a field is prefilled with when switched on (decisions 51–53). */
  sequenceType: BotSequenceType;
  offsetDays: number;
  disabled: boolean;
  /** The earlier day whose edits carry forward when this day picks the template. */
  getPriorStep?: (template: BotSequenceContentTemplate) => BotSequenceConfigStep | undefined;
  onChange: (next: BotSequenceConfigStepDelivery | undefined) => void;
}

interface ContentModeRadioProps {
  mode: "generated" | "verbatim";
  disabled: boolean;
  onChange: (mode: "generated" | "verbatim") => void;
}

function ContentModeRadio({ mode, disabled, onChange }: ContentModeRadioProps): React.ReactElement {
  return (
    <div className="body-sm flex items-center gap-6 text-grey-900">
      <label className="flex cursor-pointer items-center gap-2">
        <input type="radio" checked={mode === "verbatim"} disabled={disabled} onChange={() => onChange("verbatim")} className="accent-orange-500" />
        Exact wording
      </label>
      <label className="flex cursor-pointer items-center gap-2">
        <input type="radio" checked={mode === "generated"} disabled={disabled} onChange={() => onChange("generated")} className="accent-orange-500" />
        Written by the assistant
      </label>
    </div>
  );
}

/**
 * One channel of a day: on/off, written by the assistant (optional guidance) or exact wording
 * per language; SMS adds the multi-segment allowance, Voice adds the voicemail setting
 * (ENG-3216 decisions 13/35).
 */
export function DeliveryEditor({
  channel,
  delivery,
  sequenceType,
  offsetDays,
  disabled,
  getPriorStep,
  onChange
}: DeliveryEditorProps): React.ReactElement {
  const isVoice = channel === Channel.VOICE;
  const title = isVoice ? "Voice call" : "SMS";
  const on = delivery !== undefined;
  const generated = delivery?.content.mode === "generated" ? delivery.content : undefined;
  const verbatim = delivery?.content.mode === "verbatim" ? delivery.content : undefined;
  const defaults = defaultCopyFor(sequenceType, offsetDays);
  const defaultBody = isVoice ? defaults.voicemail : defaults.sms;
  const voicemailDefault = defaultCopyFor(sequenceType, offsetDays, generated?.template).voicemail;
  const isCustomized =
    generated?.guidance !== undefined && generated.guidance !== templateGuidance(sequenceType, offsetDays, channel, generated.template);

  // Text starts as exact wording; a call starts assistant-written with voicemail on.
  const handleToggle = (next: boolean): void => {
    if (!next) {
      onChange(undefined);
      return;
    }
    onChange(defaultDelivery(channel, defaults));
  };

  // Switching mode never leaves an empty field behind: the default wording or guidance fills it.
  const handleModeChange = (mode: "generated" | "verbatim"): void => {
    if (!delivery) return;
    const existingGuidance = delivery.content.mode === "generated" ? delivery.content.guidance : undefined;
    const existingBodies = delivery.content.mode === "verbatim" ? delivery.content.bodies : undefined;
    onChange({
      ...delivery,
      content:
        mode === "generated"
          ? { mode: "generated", guidance: existingGuidance?.trim() ? existingGuidance : defaults.guidance }
          : {
              mode: "verbatim",
              bodies: existingBodies?.[BOT_SEQUENCE_DEFAULT_BODY_LOCALE]?.trim()
                ? existingBodies
                : { ...existingBodies, [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: defaultBody }
            }
    });
  };

  const handleGuidanceChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    if (!delivery || delivery.content.mode !== "generated") return;
    onChange({ ...delivery, content: { ...delivery.content, guidance: event.target.value } });
  };

  const setTemplateGuidance = (template: BotSequenceContentTemplate | undefined, prior?: BotSequenceConfigStep): void => {
    if (!delivery) return;
    const guidance = templateGuidance(sequenceType, offsetDays, channel, template, prior);
    // The voicemail follows the template too, unless the operator wrote their own.
    const voicemail = isVoice
      ? voicemailAfterTemplate(delivery.voicemail, voicemailDefault, defaultCopyFor(sequenceType, offsetDays, template).voicemail)
      : delivery.voicemail;
    onChange({ ...delivery, content: { mode: "generated", guidance, template }, ...(voicemail ? { voicemail } : {}) });
  };

  const handleRevertToDefault = (): void => setTemplateGuidance(generated?.template);

  const handleTemplateChange = (template: BotSequenceContentTemplate | undefined): void => {
    if (!delivery || delivery.content.mode !== "generated") return;
    const { guidance, template: current } = delivery.content;
    const isEdited = Boolean(guidance?.trim()) && guidance !== templateGuidance(sequenceType, offsetDays, channel, current);
    if (confirmTemplateSwitch(isEdited)) setTemplateGuidance(template, template && getPriorStep?.(template));
  };

  const handleBodiesChange = (bodies: Record<string, string>): void => {
    if (!delivery) return;
    onChange({ ...delivery, content: { mode: "verbatim", bodies } });
  };

  const handleAllowSplit = (allowSplit: boolean): void => {
    if (!delivery) return;
    onChange({ ...delivery, allowSplit });
  };

  const handleVoicemailToggle = (enabled: boolean): void => {
    if (!delivery) return;
    const bodies = delivery.voicemail?.bodies;
    const prefilled = bodies?.[BOT_SEQUENCE_DEFAULT_BODY_LOCALE]?.trim()
      ? bodies
      : { ...bodies, [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: voicemailDefault };
    onChange({ ...delivery, voicemail: { enabled, bodies: prefilled } });
  };

  const handleVoicemailBodies = (bodies: Record<string, string>): void => {
    if (!delivery) return;
    onChange({ ...delivery, voicemail: { enabled: delivery.voicemail?.enabled ?? true, bodies } });
  };

  const defaultBodies = (text: string): Record<string, string> => ({ [BOT_SEQUENCE_DEFAULT_BODY_LOCALE]: text });

  return (
    <section className="rounded-lg border border-grey-200 bg-white">
      <div className="flex items-center justify-between gap-4 px-4 py-3">
        <div className="body-sm-alt text-grey-900">{title}</div>
        <Switch on={on} onSet={handleToggle} isDisabled={disabled} activeColor="orange" />
      </div>
      {delivery && (
        <div className="flex flex-col gap-3 border-t border-grey-100 px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <ContentModeRadio mode={delivery.content.mode} disabled={disabled} onChange={handleModeChange} />
            <RevertToDefault
              isCustomized={verbatim ? !bodiesAreDefault(verbatim.bodies, defaultBody) : isCustomized}
              disabled={disabled}
              onRevert={verbatim ? () => handleBodiesChange(defaultBodies(defaultBody)) : handleRevertToDefault}
            />
          </div>
          {delivery.content.mode === "generated" ? (
            <>
              <TemplateSelect value={delivery.content.template} disabled={disabled} onChange={handleTemplateChange} />
              <TextArea
                value={delivery.content.guidance ?? ""}
                onChange={handleGuidanceChange}
                disabled={disabled}
                rows={3}
                maxLength={BOT_SEQUENCE_STEP_LIMITS.guidance}
                placeholder="Anything the assistant should say or avoid"
                helperText={
                  isVoice
                    ? "The call opens with the location's disclaimer, then this, then the assistant handles the conversation live."
                    : "The assistant writes the message from the tenant's current data plus this guidance."
                }
              />
            </>
          ) : (
            <LocaleBodiesEditor
              bodies={delivery.content.bodies}
              onChange={handleBodiesChange}
              disabled={disabled}
              placeholder="Type {{ to insert a variable"
              maxLength={BOT_SEQUENCE_STEP_LIMITS.verbatimBody}
              helperText="Sent word for word. Tenants whose language has no body get the English body. A missing variable value fails this send and is recorded."
            />
          )}
          {!isVoice && delivery.content.mode === "verbatim" && (
            <label className="body-xs flex items-center gap-2 text-grey-700">
              <input
                type="checkbox"
                checked={delivery.allowSplit === true}
                disabled={disabled}
                onChange={(event) => handleAllowSplit(event.target.checked)}
                className="accent-orange-500"
              />
              Allow more than one text segment
            </label>
          )}
          {isVoice && (
            <div className="flex flex-col gap-3 border-t border-grey-100 pt-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="body-sm-alt text-grey-900">Leave a voicemail if nobody answers</div>
                  <div className="body-xs text-grey-600">Spoken after the beep, word for word. Off = the bot hangs up when it reaches a machine.</div>
                </div>
                <div className="flex shrink-0 items-center gap-3">
                  {delivery.voicemail?.enabled && (
                    <RevertToDefault
                      isCustomized={!bodiesAreDefault(delivery.voicemail.bodies, voicemailDefault)}
                      disabled={disabled}
                      onRevert={() => handleVoicemailBodies(defaultBodies(voicemailDefault))}
                    />
                  )}
                  <Switch on={delivery.voicemail?.enabled === true} onSet={handleVoicemailToggle} isDisabled={disabled} activeColor="orange" />
                </div>
              </div>
              {delivery.voicemail?.enabled && (
                <LocaleBodiesEditor
                  bodies={delivery.voicemail.bodies}
                  onChange={handleVoicemailBodies}
                  disabled={disabled}
                  placeholder="Type {{ to insert a variable"
                  maxLength={BOT_SEQUENCE_STEP_LIMITS.verbatimBody}
                  helperText="Exact wording only."
                />
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
